// Movement: squad-level pathing (grid A* on the squad centroid) + per-model micro
// (formation slots, cover-slot pull, separation) with posture assignment. Combat may
// later override posture to "firing"/"melee" in the same tick.

import { angleOf, dist, norm, rotToward, type Vec2 } from "../engine/vec";
import { assignCoverForSettling, threatDir } from "./cover";
import type { Model, Squad } from "./types";
import { addRot, formationOffsets, TUNE, World } from "./world";

export function stepSteering(world: World, dt: number): void {
  if (world.tickCount % 6 === 0) assignCoverForSettling(world, TUNE.coverSnapRadius);

  for (const sq of world.squads) {
    if (sq.dead) continue;
    sq.repathTimer -= dt;
    resolveSquadMovement(world, sq, dt);
  }
  for (const sq of world.squads) {
    if (sq.dead) continue;
    stepModels(world, sq, dt);
  }
}

function squadSpeed(world: World, sq: Squad): number {
  let s = sq.def.moveSpeed;
  const army = world.armies[sq.faction];
  if (sq.faction === "kindled") s *= 1 + army.fervorTier * 0.06;
  if (sq.suppression >= TUNE.suppressionProne) s *= 0.15;
  else if (sq.suppression >= TUNE.suppressionPinned) s *= 0.5;
  if (sq.stance === "retreat") s *= 1.25;
  if (sq.broken) s *= 1.25;
  if (sq.frenzy) s *= 1.45;
  return s;
}

function ensurePath(world: World, sq: Squad, dest: Vec2): void {
  const changed = !sq.pathTarget || dist(sq.pathTarget, dest) > world.map.cell;
  const stalled = sq.path.length === 0 && dist(sq.center, dest) > world.map.cell && sq.repathTimer <= 0;
  if (changed || stalled) {
    sq.path = world.grid.findPath(sq.center, dest);
    sq.pathTarget = { ...dest };
    sq.repathTimer = 0.5;
    // drop the first waypoint if we're basically already on it
    if (sq.path.length > 1 && dist(sq.center, sq.path[0]) < world.map.cell) sq.path.shift();
  }
}

/** Move the squad centroid toward `dest`; returns true when within `arrive`. */
function moveCenter(world: World, sq: Squad, dest: Vec2, speed: number, dt: number, arrive: number): boolean {
  ensurePath(world, sq, dest);
  let wp = sq.path.length > 0 ? sq.path[0] : dest;
  if (sq.path.length > 0 && dist(sq.center, wp) <= TUNE.moveArrive) {
    sq.path.shift();
    wp = sq.path.length > 0 ? sq.path[0] : dest;
  }
  const dd = dist(sq.center, wp);
  const finalDist = dist(sq.center, dest);
  if (finalDist <= arrive && sq.path.length === 0) return true;
  if (dd < 0.5) return finalDist <= arrive;
  const dir = norm({ x: wp.x - sq.center.x, y: wp.y - sq.center.y });
  const step = Math.min(dd, speed * dt);
  sq.center.x += dir.x * step;
  sq.center.y += dir.y * step;
  sq.facing = rotToward(sq.facing, angleOf(dir), 3.5 * dt);
  return false;
}

function haltPath(sq: Squad): void {
  sq.path = [];
  sq.pathTarget = null;
}

function faceThreatOrOrder(world: World, sq: Squad, dt: number): void {
  let want: number;
  if (sq.order.facing !== null) want = sq.order.facing;
  else want = threatDir(world, sq);
  sq.facing = rotToward(sq.facing, want, 3 * dt);
}

function resolveSquadMovement(world: World, sq: Squad, dt: number): void {
  const speed = squadSpeed(world, sq);

  // Broken/frenzy override all orders.
  if (sq.frenzy) {
    const enemy = world.nearestEnemySquad(sq.faction, sq.center, 2000);
    if (enemy) moveCenter(world, sq, enemy.center, speed, dt, sq.def.meleeWeapon.range);
    return;
  }
  if (sq.broken) {
    const safe = fleeTarget(world, sq);
    moveCenter(world, sq, safe, speed, dt, 30);
    return;
  }

  const o = sq.order;
  const wpn = sq.def.weapon;
  const charger = !!sq.def.meleeCharger;

  switch (o.type) {
    case "stop":
    case "hold": {
      haltPath(sq);
      faceThreatOrOrder(world, sq, dt);
      return;
    }
    case "move": {
      if (o.pos) {
        const arrived = moveCenter(world, sq, o.pos, speed, dt, TUNE.moveArrive);
        if (arrived) {
          haltPath(sq);
          if (o.facing !== null) sq.facing = rotToward(sq.facing, o.facing, 3 * dt);
        }
      }
      return;
    }
    case "attackmove": {
      const enemy = world.nearestEnemySquad(sq.faction, sq.center, sq.def.sightRange);
      if (enemy) {
        const d = dist(sq.center, enemy.center);
        if (charger && d < sq.def.sightRange) {
          moveCenter(world, sq, enemy.center, speed, dt, sq.def.meleeWeapon.range);
          return;
        }
        if (d <= wpn.range * 0.92) {
          haltPath(sq);
          faceThreatOrOrder(world, sq, dt);
          return;
        }
        // advance toward the enemy to get in range
        moveCenter(world, sq, enemy.center, speed, dt, wpn.range * 0.85);
        return;
      }
      if (o.pos) {
        const arrived = moveCenter(world, sq, o.pos, speed, dt, TUNE.moveArrive);
        if (arrived) haltPath(sq);
      }
      return;
    }
    case "attack": {
      const target = world.squads.find((s) => s.id === o.targetSquad && !s.dead);
      if (!target) {
        sq.order = { type: "hold", pos: { ...sq.center }, facing: sq.facing, targetSquad: -1, targetPoint: -1 };
        return;
      }
      const d = dist(sq.center, target.center);
      const hold = charger ? sq.def.meleeWeapon.range * 0.9 : wpn.range * 0.9;
      if (d <= hold) {
        haltPath(sq);
        sq.facing = rotToward(sq.facing, angleOf({ x: target.center.x - sq.center.x, y: target.center.y - sq.center.y }), 3 * dt);
      } else {
        moveCenter(world, sq, target.center, speed, dt, hold);
      }
      return;
    }
    case "capture": {
      const pt = world.points[o.targetPoint];
      if (!pt) {
        sq.order.type = "hold";
        return;
      }
      const d = dist(sq.center, pt.pos);
      if (d <= pt.captureRadius * 0.55) {
        haltPath(sq);
        faceThreatOrOrder(world, sq, dt);
      } else {
        moveCenter(world, sq, pt.pos, speed, dt, pt.captureRadius * 0.5);
      }
      return;
    }
  }
}

/** Where a broken squad runs: toward the nearest friendly structure, else away from foes. */
function fleeTarget(world: World, sq: Squad): Vec2 {
  let best: Vec2 | null = null;
  let bd = Infinity;
  for (const s of world.structures) {
    if (s.faction !== sq.faction) continue;
    const d = dist(s.pos, sq.center);
    if (d < bd) {
      bd = d;
      best = s.pos;
    }
  }
  const enemy = world.nearestEnemySquad(sq.faction, sq.center, 2000);
  if (best && bd < 700) return best;
  if (enemy) {
    const away = norm({ x: sq.center.x - enemy.center.x, y: sq.center.y - enemy.center.y });
    return { x: sq.center.x + away.x * 300, y: sq.center.y + away.y * 300 };
  }
  return best ?? sq.center;
}

function stepModels(world: World, sq: Squad, dt: number): void {
  const offs = formationOffsets(sq.def.modelCount, sq.def.role === "vehicle" || sq.def.role === "hero" ? 0 : 24);
  const alive = sq.models.filter((m) => m.alive);
  const moving = sq.path.length > 0 || sq.broken || sq.frenzy;

  for (const m of alive) {
    if (m.meleeTarget !== -1) {
      // combat manages melee positioning; just keep posture readable
      m.posture = sq.frenzy ? "frenzied" : "melee";
      continue;
    }

    const slotLocal = offs[Math.min(m.slotIndex, offs.length - 1)] ?? { x: 0, y: 0 };
    const formationGoal = addRot(sq.center, slotLocal, sq.facing);
    const goal = m.coverTarget && !sq.broken && !sq.frenzy && !moving ? m.coverTarget : formationGoal;

    const d = dist(m.pos, goal);
    let speed = squadSpeed(world, sq);
    // small separation so models don't stack
    let sepx = 0;
    let sepy = 0;
    for (const o of alive) {
      if (o === m) continue;
      const dx = m.pos.x - o.pos.x;
      const dy = m.pos.y - o.pos.y;
      const dd = dx * dx + dy * dy;
      if (dd > 0 && dd < 16 * 16) {
        const inv = 1 / Math.sqrt(dd);
        sepx += dx * inv;
        sepy += dy * inv;
      }
    }

    if (d > 0.75) {
      const dir = norm({ x: goal.x - m.pos.x, y: goal.y - m.pos.y });
      const vx = dir.x + sepx * 0.5;
      const vy = dir.y + sepy * 0.5;
      const vn = norm({ x: vx, y: vy });
      const step = Math.min(d, speed * dt);
      m.pos.x += vn.x * step;
      m.pos.y += vn.y * step;
      m.facing = rotToward(m.facing, angleOf(vn), 6 * dt);
      pushOutOfBuildings(world, m);
    } else if (sepx || sepy) {
      const vn = norm({ x: sepx, y: sepy });
      m.pos.x += vn.x * Math.min(6 * dt * 4, 6);
      m.pos.y += vn.y * Math.min(6 * dt * 4, 6);
    }

    assignPosture(sq, m, d, moving);
  }
}

function assignPosture(sq: Squad, m: Model, distToGoal: number, moving: boolean): void {
  if (sq.frenzy) {
    m.posture = "frenzied";
    return;
  }
  if (sq.broken) {
    m.posture = "broken";
    return;
  }
  if (sq.suppression >= TUNE.suppressionProne) {
    m.posture = "prone";
    return;
  }
  if (sq.suppression >= TUNE.suppressionPinned) {
    m.posture = "pinned";
    return;
  }
  if (moving && distToGoal > 6) {
    m.posture = "advancing";
    return;
  }
  if (m.coverTarget && distToGoal < 14) {
    m.posture = "incover";
    return;
  }
  m.posture = "idle";
}

function pushOutOfBuildings(world: World, m: Model): void {
  for (const c of world.cover) {
    if (c.kind !== "building" || c.garrison) continue;
    const x = c.x!;
    const y = c.y!;
    const w = c.w!;
    const h = c.h!;
    if (m.pos.x > x - 6 && m.pos.x < x + w + 6 && m.pos.y > y - 6 && m.pos.y < y + h + 6) {
      const dl = m.pos.x - (x - 6);
      const dr = x + w + 6 - m.pos.x;
      const dt2 = m.pos.y - (y - 6);
      const db = y + h + 6 - m.pos.y;
      const min = Math.min(dl, dr, dt2, db);
      if (min === dl) m.pos.x = x - 6;
      else if (min === dr) m.pos.x = x + w + 6;
      else if (min === dt2) m.pos.y = y - 6;
      else m.pos.y = y + h + 6;
    }
  }
}
